import React, { useState, useEffect, useMemo } from "react";
import useMobile from "../hooks/useMobile";
import { Link } from "react-router-dom";
import {
  ArrowRight, Play, Award, BookOpen, CheckCircle2,
  Bell, X, Copy, Loader2, TrendingUp, Eye,
  ChevronUp, ChevronDown,
} from "lucide-react";
import AppLayout from "../components/AppLayout";
import { api } from "../api/client";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

// ─── helpers ────────────────────────────────────────────────────────────────

const fmtDate = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("en-GB", {
      day: "numeric", month: "short", year: "numeric",
    });
  } catch {
    return "—";
  }
};

const STATUS_CONFIG = {
  Completed:    { label: "Completed",    bg: "bg-lime/10",    text: "text-lime",     dot: "bg-lime" },
  "In Progress":{ label: "In Progress",  bg: "bg-grape/10",   text: "text-[#7C5CFF]",dot: "bg-[#7C5CFF]" },
  "Not Started":{ label: "Not Started",  bg: "bg-white/[0.05]",text: "text-gray-400", dot: "bg-gray-600" },
};

function statusFor(progress) {
  if (progress >= 100) return "Completed";
  if (progress > 0)    return "In Progress";
  return "Not Started";
}

function StatusBadge({ progress }) {
  const key = statusFor(progress);
  const s = STATUS_CONFIG[key];
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold ${s.bg} ${s.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
}

// ─── sub-components ─────────────────────────────────────────────────────────

function StatCard({ icon: Icon, value, label, accent }) {
  return (
    <div className="bg-white/[0.03] border border-white/[0.08] rounded-2xl p-5">
      <Icon size={16} style={{ color: accent }} className="mb-3" />
      <div className="text-3xl font-black">{value}</div>
      <div className="text-xs text-gray-500 mt-1">{label}</div>
    </div>
  );
}

function EnrolledCard({ course }) {
  const s = STATUS_CONFIG[statusFor(course.progress)];
  return (
    <Link
      to={`/learn/${course.id}`}
      className="group flex flex-col bg-white/[0.03] border border-white/[0.08] rounded-2xl overflow-hidden hover:border-white/20 transition-all duration-200"
    >
      {course.thumbnail && (
        <div className="aspect-video overflow-hidden">
          <img
            src={course.thumbnail}
            alt={course.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        </div>
      )}
      <div className="p-4 flex flex-col flex-1">
        <div className="flex items-center justify-between gap-2 mb-2">
          <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider truncate">
            {course.category}
          </span>
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold shrink-0 ${s.bg} ${s.text}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
            {s.label}
          </span>
        </div>
        <h3 className="font-bold text-sm leading-snug mb-3 flex-1 line-clamp-2">{course.title}</h3>
        <div>
          <div className="flex justify-between text-[11px] mb-1.5">
            <span className="text-gray-500 truncate pr-2">{course.instructor_name}</span>
            <span className="text-gray-400 font-semibold shrink-0">{course.progress}%</span>
          </div>
          <div className="h-1 rounded-full bg-white/10 overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${course.progress >= 100 ? "bg-lime" : "bg-[#7C5CFF]"}`}
              style={{ width: `${Math.min(course.progress, 100)}%` }}
            />
          </div>
        </div>
      </div>
    </Link>
  );
}

function SortTh({ col, label, sortState, onSort }) {
  const active = sortState.col === col;
  return (
    <th
      className="text-left px-5 py-3 font-semibold cursor-pointer select-none hover:text-white transition-colors"
      onClick={() => onSort(col)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <span className={active ? "text-lime" : "text-gray-700"}>
          {active && sortState.dir === "asc"
            ? <ChevronUp size={12} />
            : <ChevronDown size={12} />}
        </span>
      </span>
    </th>
  );
}

function EmptyState({ icon: Icon, heading, sub }) {
  return (
    <div className="flex flex-col items-center justify-center py-14 text-center border border-white/[0.06] rounded-2xl">
      <Icon className="w-9 h-9 text-gray-700 mb-3" />
      <p className="text-gray-400 font-semibold text-sm">{heading}</p>
      {sub && <p className="text-xs text-gray-600 mt-1 max-w-xs">{sub}</p>}
    </div>
  );
}

// ─── main page ──────────────────────────────────────────────────────────────

export default function Dashboard() {
  const { user } = useAuth();
  const isMobile = useMobile();
  const [enrollments, setEnrollments] = useState([]);
  const [certs, setCerts]             = useState([]);
  const [quizMap, setQuizMap]         = useState({});
  const [loading, setLoading]         = useState(true);
  const [courseFilter, setCourseFilter] = useState("All");
  const [dismissed, setDismissed]     = useState(() => new Set());
  const [sortHistory, setSortHistory] = useState({ col: "enrolled_at", dir: "desc" });

  const firstName = user?.name?.split(" ")[0] || "Welcome";
  const hour      = new Date().getHours();
  const greeting  = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";

  useEffect(() => {
    Promise.all([
      api.get("/enrollments"),
      api.get("/certificates"),
      api.get("/my/quiz-attempts").catch(() => []),
    ]).then(([enrs, cs, qa]) => {
      setEnrollments(Array.isArray(enrs) ? enrs : []);
      setCerts(Array.isArray(cs) ? cs : []);
      const qm = {};
      (qa || []).forEach((a) => { qm[a.course_id] = a.percent; });
      setQuizMap(qm);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  // ── derived state ────────────────────────────────────────────────────────
  const inProgress     = useMemo(() => enrollments.filter((e) => e.progress > 0 && e.progress < 100), [enrollments]);
  const completedEnrs  = useMemo(() => enrollments.filter((e) => e.progress >= 100), [enrollments]);
  const continueCourse = inProgress[0] ?? null;

  const filteredCourses = useMemo(() => {
    if (courseFilter === "In Progress") return enrollments.filter((e) => e.progress > 0 && e.progress < 100);
    if (courseFilter === "Completed")   return enrollments.filter((e) => e.progress >= 100);
    if (courseFilter === "Not Started") return enrollments.filter((e) => e.progress === 0);
    return enrollments;
  }, [enrollments, courseFilter]);

  const notifications = useMemo(() => {
    const notes = [];
    enrollments
      .filter((e) => e.progress === 0)
      .forEach((e) => notes.push({
        id: `assign-${e.id}`,
        type: "assignment",
        title: "New course assigned",
        body: e.title,
      }));
    certs.forEach((c) => notes.push({
      id: `cert-${c.id}`,
      type: "cert",
      title: "Certificate issued",
      body: c.course_title || c.course_name || "—",
    }));
    return notes.filter((n) => !dismissed.has(n.id));
  }, [enrollments, certs, dismissed]);

  const sortedHistory = useMemo(() => {
    const list = completedEnrs.slice();
    const { col, dir } = sortHistory;
    list.sort((a, b) => {
      const av = col === "enrolled_at" ? (a.enrolled_at || "")
               : col === "progress"    ? a.progress
               : col === "score"       ? (quizMap[a.id] ?? -1)
               : 0;
      const bv = col === "enrolled_at" ? (b.enrolled_at || "")
               : col === "progress"    ? b.progress
               : col === "score"       ? (quizMap[b.id] ?? -1)
               : 0;
      if (av < bv) return dir === "asc" ? -1 : 1;
      if (av > bv) return dir === "asc" ?  1 : -1;
      return 0;
    });
    return list;
  }, [completedEnrs, sortHistory, quizMap]);

  const toggleSort = (col) =>
    setSortHistory((prev) =>
      prev.col === col
        ? { col, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { col, dir: "desc" }
    );

  const dismissOne  = (id) => setDismissed((prev) => new Set([...prev, id]));
  const dismissAll  = ()   => setDismissed((prev) => new Set([...prev, ...notifications.map((n) => n.id)]));

  // ── loading ──────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <AppLayout searchPlaceholder="Search courses, lessons...">
        <div className="flex items-center justify-center py-28 text-gray-500">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading your dashboard…
        </div>
      </AppLayout>
    );
  }

  // ── render ───────────────────────────────────────────────────────────────
  return (
    <AppLayout searchPlaceholder="Search courses, lessons...">

      {/* ── Greeting ──────────────────────────────────────────────────────── */}
      <div className="mb-8">
        <p className="text-sm text-gray-500 mb-1">{greeting},</p>
        <h1 className="text-5xl font-black tracking-tight leading-none">
          {firstName}<span className="text-[#7C5CFF]">.</span>
        </h1>
      </div>

      {/* ── Stats ─────────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <StatCard icon={BookOpen}     value={enrollments.length} label="Enrolled"      accent="#CFFF50" />
        <StatCard icon={Play}         value={inProgress.length}  label="In Progress"   accent="#7C5CFF" />
        <StatCard icon={CheckCircle2} value={completedEnrs.length} label="Completed"   accent="#CFFF50" />
        <StatCard icon={Award}        value={certs.length}       label="Certificates"  accent="#7C5CFF" />
      </div>

      {/* ── Continue Learning ─────────────────────────────────────────────── */}
      {continueCourse && (
        <div className="relative overflow-hidden rounded-2xl border border-white/[0.08] mb-8 min-h-[200px]">
          {continueCourse.thumbnail && (
            <div className="absolute inset-0">
              <img
                src={continueCourse.thumbnail}
                alt=""
                className="w-full h-full object-cover opacity-[0.18]"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[#0E0F12] via-[#0E0F12]/95 to-[#0E0F12]/60" />
            </div>
          )}
          <div className="relative p-8">
            <p className="text-[10px] uppercase tracking-[0.18em] text-lime font-bold mb-3">
              Continue learning
            </p>
            <span className="inline-block text-xs font-semibold px-2.5 py-1 rounded-full bg-white/[0.06] border border-white/10 text-gray-400 mb-3">
              {continueCourse.category}
            </span>
            <h2 className="text-2xl md:text-3xl font-black tracking-tight leading-tight max-w-xl mb-1">
              {continueCourse.title}
            </h2>
            <p className="text-sm text-gray-500 mb-5">{continueCourse.instructor_name}</p>
            <div className="max-w-sm mb-5">
              <div className="flex justify-between text-xs mb-1.5">
                <span className="text-gray-400">Progress</span>
                <span className="text-lime font-bold">{continueCourse.progress}%</span>
              </div>
              <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-[#7C5CFF] to-lime"
                  style={{ width: `${continueCourse.progress}%` }}
                />
              </div>
            </div>
            <Link
              to={`/learn/${continueCourse.id}`}
              data-testid="continue-learning-btn"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-lime text-[#0E0F12] font-bold text-sm hover:bg-[#d8ff6a] transition-colors"
            >
              Resume <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      )}

      {/* ── Assigned Courses ──────────────────────────────────────────────── */}
      <section className="mb-8">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h2 className="text-xl font-bold">Assigned Courses</h2>
          <div className="flex gap-1">
            {["All", "In Progress", "Not Started", "Completed"].map((f) => (
              <button
                key={f}
                onClick={() => setCourseFilter(f)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                  courseFilter === f
                    ? "bg-white text-[#0E0F12]"
                    : "text-gray-400 hover:text-white"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        {filteredCourses.length === 0 ? (
          <EmptyState
            icon={BookOpen}
            heading={enrollments.length === 0 ? "No courses assigned yet" : `No courses with status "${courseFilter}"`}
            sub={enrollments.length === 0 ? "Courses assigned by your administrator will appear here." : undefined}
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredCourses.map((c) => <EnrolledCard key={c.id} course={c} />)}
          </div>
        )}
      </section>

      {/* ── Certificates + Notifications ──────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 mb-8">

        {/* Certificates */}
        <section className="lg:col-span-3 flex flex-col">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold">Certificates</h2>
            <Link to="/certificates" className="text-xs text-lime font-semibold hover:underline">
              View all
            </Link>
          </div>
          {certs.length === 0 ? (
            <EmptyState
              icon={Award}
              heading="No certificates yet"
              sub="Complete a course to earn your first certificate."
            />
          ) : (
            <div className="space-y-2">
              {certs.slice(0, 5).map((c) => (
                <div
                  key={c.id}
                  className="flex items-center gap-3 bg-white/[0.03] border border-white/[0.08] rounded-xl px-4 py-3"
                >
                  <div className="w-9 h-9 rounded-full bg-lime/10 flex items-center justify-center shrink-0">
                    <Award size={16} className="text-lime" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm truncate">
                      {c.course_title || c.course_name || "—"}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Issued {fmtDate(c.issued_at)}
                      {c.code ? ` · ${c.code}` : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Link
                      to={`/certificates/${c.id}`}
                      title="View certificate"
                      className="p-1.5 rounded-lg hover:bg-white/[0.06] text-gray-500 hover:text-white transition-colors"
                    >
                      <Eye size={14} />
                    </Link>
                    <button
                      title="Copy verification link"
                      onClick={() => {
                        navigator.clipboard.writeText(`${window.location.origin}/certificates/${c.id}`);
                        toast.success("Verification link copied");
                      }}
                      className="p-1.5 rounded-lg hover:bg-white/[0.06] text-gray-500 hover:text-white transition-colors"
                    >
                      <Copy size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Notifications */}
        <section className="lg:col-span-2 flex flex-col">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold flex items-center gap-2">
              Notifications
              {notifications.length > 0 && (
                <span className="text-[10px] font-bold bg-lime text-[#0E0F12] rounded-full px-2 py-0.5 leading-none">
                  {notifications.length}
                </span>
              )}
            </h2>
            {notifications.length > 0 && (
              <button
                onClick={dismissAll}
                className="text-xs text-gray-500 hover:text-white transition-colors"
              >
                Clear all
              </button>
            )}
          </div>
          {notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center flex-1 py-12 border border-white/[0.06] rounded-2xl text-center min-h-[140px]">
              <Bell className="w-8 h-8 text-gray-700 mb-2" />
              <p className="text-sm text-gray-500">All clear</p>
            </div>
          ) : (
            <div className="space-y-2">
              {notifications.map((n) => (
                <div
                  key={n.id}
                  className="flex items-start gap-3 bg-white/[0.03] border border-white/[0.08] rounded-xl px-3.5 py-3"
                >
                  <span
                    className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${
                      n.type === "cert" ? "bg-lime" : "bg-[#7C5CFF]"
                    }`}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-gray-300">{n.title}</p>
                    <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{n.body}</p>
                  </div>
                  <button
                    onClick={() => dismissOne(n.id)}
                    className="text-gray-600 hover:text-gray-400 transition-colors shrink-0 mt-0.5"
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      {/* ── Training History ──────────────────────────────────────────────── */}
      <section className="mb-4">
        <h2 className="text-xl font-bold mb-4">Training History</h2>
        {completedEnrs.length === 0 ? (
          <EmptyState
            icon={TrendingUp}
            heading="No completed courses yet"
            sub="Completed courses and quiz scores will appear here."
          />
        ) : isMobile ? (
          /* Mobile: card list */
          <div className="flex flex-col gap-3">
            {sortedHistory.map((c) => {
              const cert  = certs.find((x) => x.course_id === c.id);
              const score = quizMap[c.id];
              return (
                <div key={c.id} className="border border-white/[0.08] rounded-2xl p-4 bg-white/[0.02]">
                  <div className="font-semibold text-white leading-snug mb-0.5">{c.title}</div>
                  {c.category && <div className="text-xs text-gray-500 mb-3">{c.category}</div>}
                  <div className="flex flex-wrap gap-x-4 gap-y-2 text-xs text-gray-400">
                    <span>Enrolled: {fmtDate(c.enrolled_at)}</span>
                    <StatusBadge progress={c.progress} />
                    {score !== undefined && (
                      <span className={`font-semibold ${score >= 60 ? "text-lime" : "text-red-400"}`}>
                        Quiz: {score}%
                      </span>
                    )}
                    {cert && (
                      <Link
                        to={`/certificates/${cert.id}`}
                        className="inline-flex items-center gap-1 text-lime font-semibold hover:underline"
                      >
                        <Eye size={12} /> Certificate
                      </Link>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* Desktop: table */
          <div className="border border-white/[0.08] rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/[0.08] text-xs text-gray-500 uppercase tracking-wider">
                  <th className="text-left px-5 py-3 font-semibold">Course</th>
                  <SortTh col="enrolled_at" label="Enrolled"   sortState={sortHistory} onSort={toggleSort} />
                  <SortTh col="progress"    label="Status"     sortState={sortHistory} onSort={toggleSort} />
                  <SortTh col="score"       label="Quiz Score" sortState={sortHistory} onSort={toggleSort} />
                  <th className="text-left px-5 py-3 font-semibold">Certificate</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/[0.05]">
                {sortedHistory.map((c) => {
                  const cert  = certs.find((x) => x.course_id === c.id);
                  const score = quizMap[c.id];
                  return (
                    <tr key={c.id} className="hover:bg-white/[0.02] transition-colors">
                      <td className="px-5 py-4">
                        <div className="font-semibold text-white leading-snug">{c.title}</div>
                        <div className="text-xs text-gray-500 mt-0.5">{c.category}</div>
                      </td>
                      <td className="px-5 py-4 text-gray-400 text-sm">{fmtDate(c.enrolled_at)}</td>
                      <td className="px-5 py-4">
                        <StatusBadge progress={c.progress} />
                      </td>
                      <td className="px-5 py-4">
                        {score !== undefined ? (
                          <span className={`font-semibold ${score >= 60 ? "text-lime" : "text-red-400"}`}>
                            {score}%
                          </span>
                        ) : (
                          <span className="text-gray-600">—</span>
                        )}
                      </td>
                      <td className="px-5 py-4">
                        {cert ? (
                          <Link
                            to={`/certificates/${cert.id}`}
                            className="inline-flex items-center gap-1 text-lime text-xs font-semibold hover:underline"
                          >
                            <Eye size={12} /> View
                          </Link>
                        ) : (
                          <span className="text-gray-600 text-xs">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

    </AppLayout>
  );
}
